import { NexudusService } from '@/services/nexudus/nexudusService';
import { AnalyticsFlag } from '@/interfaces/analytics/flags';

/**
 * Helps with configuring redirect URLs so they're not tightly coupled to a particular authentication service.
 */
export class RedirectBuilder {
  constructor(
    public readonly baseUrl: string,
    public readonly nexudusRedirectUrl: string,
  ) {}

  public buildRedirectUrl(path: string, searchParams?: Record<string, string>): string {
    if (searchParams && Object.keys(searchParams).length > 0) {
      const search = new URLSearchParams(searchParams).toString();
      return `${this.baseUrl}${path}?${search}`;
    }
    return `${this.baseUrl}${path}`;
  }

  public buildStartSignupUrl(reset: boolean = false): string {
    return reset ? this.buildRedirectUrl('/signup/reset') : this.buildRedirectUrl('/signup/personal');
  }

  public buildStartLoginUrl(redirect?: string): string {
    return this.buildRedirectUrl('/login', redirect ? { redirect } : undefined);
  }

  public buildResetPasswordRedirectUrl(stateToken: string): string {
    return this.buildRedirectUrl(`/password/reset?state=${encodeURIComponent(stateToken)}`);
  }

  public buildExpiredPasswordRedirectUrl(stateToken: string): string {
    return this.buildRedirectUrl(`/password-reset?state=${encodeURIComponent(stateToken)}`);
  }

  /**
   * The base URL for the web portal. This is where we redirect to after a successful login on the web.
   */
  public buildUrlForPortal(redirect?: string): string {
    return redirect ? `${this.nexudusRedirectUrl}?redirect=${redirect}` : this.nexudusRedirectUrl;
  }

  /**
   * The URL for the passport login success page.
   *
   * This page runs Javascript to send a message to the parent app which will (hopefully) close the webview.
   */
  public buildUrlForPassportLoginSuccess(): string {
    return this.buildRedirectUrl('/login/passport/success');
  }

  public buildSetHomeLocationUrl(redirectUrl: string, coworkerId: number): string {
    return this.buildRedirectUrl(`/set-home-location`, {
      redirect: redirectUrl,
      id: String(coworkerId),
    });
  }

  public buildUserPresentSyncUrl(redirectUrl?: string, analyticsFlags?: AnalyticsFlag[]): string {
    const params: Record<string, string> = {};
    if (redirectUrl) params.redirect = redirectUrl;
    if (analyticsFlags && analyticsFlags.length > 0) params.analyticsFlag = analyticsFlags.join(',');
    return this.buildRedirectUrl('/user-present-sync', params);
  }

  /**
   * The URL for the passport login success page.
   *
   * This page runs Javascript to send a message to the parent app which will (hopefully) close the webview.
   */
  public buildUrlForPortalLoginSuccess(redirect?: string): string {
    const searchParams = redirect ? { redirect } : undefined;
    return this.buildRedirectUrl('/login/portal/success', searchParams);
  }

  public prefixWithBaseUrl(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  static createDefault() {
    return new RedirectBuilder(
      process.env.NEXT_PUBLIC_AUTH_BASE_URL as string,
      NexudusService.defaultConfig().redirectToUrl,
    );
  }

  buildOktaOAuthCallbackUrl() {
    return this.buildRedirectUrl('/api/okta/oauth/callback');
  }

  buildUrlForPasswordResetSuccess() {
    return this.buildRedirectUrl('/forgot-password/update-password-complete');
  }

  buildUrlForExplodedUser() {
    return this.buildRedirectUrl('/notify-user-fixup');
  }

  /**
   * Sends a staged (never activated) account back into the signup flow to finish activating. The
   * route re-checks the account is still staged, seeds the signup session and lands the user on
   * the first step they still need to complete.
   */
  buildResumeVerificationUrl(email: string) {
    return this.buildRedirectUrl('/signup/personal/resumeVerification', { email });
  }
}
